import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from './authService';
import { POLL_INTERVALS, smartQueryDefaults, smartRefetchInterval } from '../utils/queryPolling';

// Query keys
export const notificationKeys = {
  all: ['notifications'],
  list: () => [...notificationKeys.all, 'list'],
  unreadCount: () => [...notificationKeys.all, 'unread-count'],
  settings: () => [...notificationKeys.all, 'settings'],
};

// API functions
export const notificationApi = {
  getNotifications: async (params = {}) => {
    const response = await api.get('/notifications', { params });
    return response.data;
  },

  markAsRead: async (notificationId) => {
    const response = await api.put(`/notifications/${notificationId}/read`);
    return response.data;
  },

  markAllAsRead: async () => {
    const response = await api.put('/notifications/read-all');
    return response.data;
  },

  deleteNotification: async (notificationId) => {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  },

  getUnreadCount: async (params = {}) => {
    const response = await api.get('/notifications/unread-count', { params });
    return response.data;
  },

  createBroadcast: async (data) => {
    const response = await api.post('/admin/broadcasts', data);
    return response.data;
  },

  getBroadcastHistory: async (params = {}) => {
    const response = await api.get('/admin/broadcasts/history', { params });
    return response.data;
  },
};

// React Query hooks
export const useNotifications = (params = {}, options = {}) => {
  return useQuery({
    queryKey: [...notificationKeys.list(), params],
    queryFn: () => notificationApi.getNotifications(params),
    staleTime: 30 * 1000,
    ...smartQueryDefaults,
    ...options,
  });
};

export const useUnreadNotificationsCount = () => {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const response = await notificationApi.getUnreadCount();
      return response.data?.count || 0;
    },
    staleTime: 30 * 1000,
    refetchInterval: smartRefetchInterval(POLL_INTERVALS.BADGE),
    ...smartQueryDefaults,
  });
};

export const useUnreadSupportTicketCount = () => {
  return useQuery({
    queryKey: [...notificationKeys.unreadCount(), 'support_ticket'],
    queryFn: async () => {
      const response = await notificationApi.getUnreadCount({ type: 'support_ticket' });
      return response.data?.count || 0;
    },
    staleTime: 30 * 1000,
    refetchInterval: smartRefetchInterval(POLL_INTERVALS.BADGE),
    ...smartQueryDefaults,
  });
};

export const useMarkNotificationAsRead = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationApi.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    ...options,
  });
};

export const useMarkAllNotificationsAsRead = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationApi.markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    ...options,
  });
};

export const useDeleteNotification = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationApi.deleteNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    ...options,
  });
};
